import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Play,
  FileText,
  Clock,
  Workflow,
  Settings,
  Database,
  Package,
  ExternalLink,
} from 'lucide-react';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';

// Navigation items matching the current structure
const navigationItems = [
  {
    title: 'Dashboard',
    href: '/dashboard',
    icon: Home,
  },
  {
    title: 'Execute Workflow',
    href: '/execute',
    icon: Play,
  },
  {
    title: 'Manage Scripts',
    href: '/scripts',
    icon: FileText,
  },
  {
    title: 'History',
    href: '/history',
    icon: Clock,
  },
];

const secondaryItems = [
  {
    title: 'Bill of Materials Management',
    href: '/tools/bom',
    icon: Package,
  },
  {
    title: 'Transaction data Explorer',
    href: '/tools/tradesparq',
    icon: Database,
  },
  {
    title: 'Crystal UI',
    href: 'https://blue-island-0d814841e.1.azurestaticapps.net/',
    icon: ExternalLink,
    isExternal: true,
  },
  {
    title: 'Workflows',
    href: '/workflows',
    icon: Workflow,
  },
  {
    title: 'Settings',
    href: '/settings',
    icon: Settings,
  },
];

export function AppSidebar() {
  const location = useLocation();

  return (
    <Sidebar className="border-r border-gray-200 bg-white">
      <SidebarHeader className="border-b border-gray-200 p-4">
        <div className="flex items-center space-x-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-purple-600">
            <span className="text-sm font-bold text-white">CW</span>
          </div>
          <div>
            <h1 className="text-sm font-semibold text-gray-900">
              Crystal Workflow
            </h1>
            <p className="text-xs text-gray-500">Internal Tool</p>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Tools */}
        <SidebarGroup>
          <SidebarGroupLabel>Tools</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {secondaryItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive}>
                      {(item as any).isExternal ? (
                        <a href={item.href} target="_blank" rel="noopener noreferrer">
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </a>
                      ) : (
                        <Link to={item.href}>
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel>Internal Tools</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild isActive={isActive}>
                      <Link to={item.href}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Status Indicator */}
        <SidebarGroup className="mt-auto">
          <SidebarGroupContent>
            <div className="flex items-center space-x-2 rounded-lg bg-green-50 p-3 border border-green-200">
              <div className="relative">
                <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                <div className="absolute inset-0 h-2 w-2 bg-green-400 rounded-full animate-ping"></div>
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-green-700">Connected</p>
                <p className="text-xs text-green-600">Backend API</p>
              </div>
            </div>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}

// Main layout component that provides the sidebar context
export function AppSidebarLayout({ children }: { children: React.ReactNode }) {
  return (
    <SidebarProvider>
      <div className="flex min-h-screen">
        <AppSidebar />
        <main className="flex-1 overflow-auto">
          <div className="flex h-16 items-center  border-gray-200 bg-white px-4 my-2">
            <SidebarTrigger className="mr-4" />
            
          </div>
          <div className="p-6">{children}</div>
        </main>
      </div>
    </SidebarProvider>
  );
}